import React, {useCallback, useEffect, useState} from 'react';
import {
    useGetJobPdfStatusQuery,
    useLazyDownloadJobPDFQuery,
} from 'components/customer/Job/store/jobApi';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {genericMessageHandler} from 'shared/helpers';
import {useNotificationContext} from 'contexts';
import {
    useGetRoomPlannerPDFStatusQuery,
    useLazyDownloadRoomPlannerPDFQuery,
} from 'components/customer/RoomPlanner/store/roomPlannerApi';

export enum PdfType {
    JOB_PDF,
    ROOM_PLANNER,
}
const usePdfAPIs = (type: PdfType, id: number, timestamp: number) => {
    const {
        data: jobDetailStatus,
        isLoading: isJobPDFStatusLoading,
        isFetching: isJobPDFStatusFetching,
    } = useGetJobPdfStatusQuery(
        {jobId: id, timestamp},
        {skip: type === PdfType.ROOM_PLANNER || !id, pollingInterval: 5000}
    );

    const {
        data: roomPlannerStatus,
        isLoading: isRoomPlannerStatusLoading,
        isFetching: isRoomPlannerStatusFetching,
    } = useGetRoomPlannerPDFStatusQuery(
        {roomId: id, timestamp},
        {skip: type === PdfType.JOB_PDF || !id, pollingInterval: 5000}
    );

    const [downloadJobPdf] = useLazyDownloadJobPDFQuery();
    const [downloadRoomPlannerPdf] = useLazyDownloadRoomPlannerPDFQuery();

    switch (type) {
        case PdfType.JOB_PDF: {
            return {
                data: jobDetailStatus,
                isLoading: isJobPDFStatusLoading,
                isFetching: isJobPDFStatusFetching,
                downloadPdf: downloadJobPdf,
                openLink: `/api/job/${id}/pdf/details`,
                generatingMessage: (
                    <>
                        <p>
                            Your PDF is being updated with the latest preview
                            images of your products. The final version will be
                            ready for export in a couple of minutes.
                        </p>
                        <i>
                            You can close this dialog and continue reviewing
                            your order details while this is happening in the
                            background.
                        </i>
                    </>
                ),
            };
        }
        case PdfType.ROOM_PLANNER: {
            return {
                data: roomPlannerStatus,
                isLoading: isRoomPlannerStatusLoading,
                isFetching: isRoomPlannerStatusFetching,
                downloadPdf: downloadRoomPlannerPdf,
                openLink: `/api/room-planner/pdf/${id}`,
                generatingMessage: (
                    <>
                        <p>
                            Your PDF is being generated. The final version will
                            be ready for export in a couple of minutes.
                        </p>
                        <i>
                            You can close this dialog and your PDF will continue
                            generating in the background.
                        </i>
                    </>
                ),
            };
        }
    }
};

export const usePdfLoadingPrompt = (type: PdfType, isDownload = true) => {
    const {dialog, showDialog} = useConfirmationDialog();
    const [pdfId, setPdfId] = useState<number>(null);
    const [timestamp, setTimestamp] = useState<number>(null);
    const [isFirstCall, setIsFirstCall] = useState(true);
    const {
        data,
        isLoading,
        isFetching,
        generatingMessage,
        downloadPdf,
        openLink,
    } = usePdfAPIs(type, pdfId, timestamp);

    const {notify} = useNotificationContext();

    const viewPdf = useCallback((id: number) => {
        setPdfId(id);
        // cache buster
        setTimestamp(Date.now());
        setIsFirstCall(true);
    }, []);

    const openUrl = useCallback(
        (id: number) => {
            setPdfId(null);
            if (isDownload) {
                void downloadPdf({id})
                    .unwrap()
                    .then((response) => {
                        if (response && response.blob) {
                            const url = URL.createObjectURL(response.blob);
                            const link = document.createElement('a');

                            const contentDisposition = response.headers.get(
                                'Content-Disposition'
                            );
                            if (contentDisposition) {
                                const filenameMatch =
                                    contentDisposition.match(
                                        /filename="?([^"]+)"?/
                                    );
                                if (filenameMatch && filenameMatch[1]) {
                                    link.download = filenameMatch[1];
                                }
                            }
                            link.href = url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                        } else {
                            genericMessageHandler(notify, {
                                message:
                                    'Could not download the PDF. Please try again.',
                            });
                        }
                    })
                    .catch(() => {
                        genericMessageHandler(notify, {
                            message:
                                'Could not download the PDF. Please try again.',
                        });
                    });
            } else {
                window.open(openLink, '_blank');
            }
        },
        [openLink]
    );

    useEffect(() => {
        // determines if pdf is available on first call
        if (isFirstCall && data && !isLoading && !isFetching) {
            setIsFirstCall(false);
            if (data && !data.isJobQueued) {
                openUrl(pdfId);
            }
        }
    }, [isFirstCall, data, pdfId, isLoading, isFetching]);

    useEffect(() => {
        if (pdfId && data && !isFirstCall) {
            const {isJobQueued} = data;
            showDialog({
                title: !isJobQueued
                    ? 'Your PDF is ready!'
                    : 'PDF Generating...',
                className: 'confirm-modal',
                message: !isJobQueued ? <div /> : generatingMessage,
                hideYesButton: true,
                hideNoButton: true,
                headerClass: !isJobQueued ? 'border-0' : undefined,
                footerClass: !isJobQueued ? 'border-0' : undefined,
                bodyClass: !isJobQueued ? 'p-0' : undefined,
                buttons: [
                    {
                        show: true,
                        controlledHideDialog: false,
                        name: 'Close',
                        variant: 'secondary',
                        action: () => {
                            setPdfId(null);
                        },
                    },
                    {
                        show: true,
                        controlledHideDialog: false,
                        name: isDownload ? 'Download PDF' : 'View PDF',
                        variant: 'primary',
                        isLoading: !!isJobQueued,
                        action: () => {
                            openUrl(pdfId);
                        },
                    },
                ],
            });
            if (!isJobQueued) {
                // stop polling if job pdf is ready
                setPdfId(null);
            }
        }
    }, [pdfId, data, isFirstCall]);

    return {
        viewPdf,
        pdfDialog: dialog,
        isLoading: !!pdfId,
    };
};
